import axios from 'axios'
import type {
  CreateInstanceRequest,
  SendTextRequest,
  SendMediaRequest,
  ProxyRequest,
  AdvancedSettings,
  FailoverGroupRequest,
  FailoverMemberRequest,
  InstanceConnectionSettings,
  StartCallRequest,
  EndCallRequest,
} from './types'

const api = axios.create({
  baseURL: window.location.origin,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

// Request interceptor: attach JWT Bearer token. Instance-key requests opt out.
api.interceptors.request.use((config) => {
  if (!config.headers['x-instance-key']) {
    const jwtToken = localStorage.getItem('neogo_token')
    if (jwtToken) {
      config.headers['Authorization'] = `Bearer ${jwtToken}`
    }
  }
  return config
})

// Helper to create config with a specific instance API key
function withInstanceKey(apikey: string) {
  return { headers: { apikey, 'x-instance-key': '1' } }
}

// Response interceptor: handle 401 with token refresh, then fallback to logout
let isRefreshing = false
let failedQueue: Array<{ resolve: (v: unknown) => void; reject: (e: unknown) => void }> = []

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Telemetry hook: record user-perceived API failures (4xx/5xx/network).
    // Skips the telemetry endpoint itself to avoid recursive metric loops.
    const requestUrl: string | undefined = originalRequest?.url
    if (requestUrl && !requestUrl.includes('/manager-api/telemetry')) {
      try {
        // Lazy import to keep the axios bootstrap free of module-init cycles.
        const { recordApiError } = await import('./telemetry')
        const status = error.response?.status ?? 0
        // Use the current route name as the scoping label since we don't know
        // which Vue route triggered this from the axios layer alone.
        const route =
          typeof window !== 'undefined' ? window.location.pathname.split('/').filter(Boolean).pop() : null
        recordApiError(route ?? null, status)
      } catch {
        // never let telemetry break the request
      }
    }

    // Don't intercept instance-specific key requests
    if (originalRequest?.headers?.['x-instance-key']) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('neogo_refresh_token')

      if (refreshToken) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          }).then((token) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`
            return api(originalRequest)
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const res = await api.post('/auth/refresh', { refreshToken })
          const { token, refreshToken: newRefresh } = res.data
          localStorage.setItem('neogo_token', token)
          localStorage.setItem('neogo_refresh_token', newRefresh)
          originalRequest.headers['Authorization'] = `Bearer ${token}`
          processQueue(null, token)
          return api(originalRequest)
        } catch (refreshError) {
          processQueue(refreshError, null)
          localStorage.removeItem('neogo_token')
          localStorage.removeItem('neogo_refresh_token')
          localStorage.removeItem('neogo_apikey')
          window.location.href = '/manager/login'
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }

      window.location.href = '/manager/login'
    }

    return Promise.reject(error)
  },
)

// --- Health & Server ---

export function getHealth() {
  return api.get('/health')
}

export function getMetricsRaw() {
  return api.get('/metrics', { responseType: 'text' as const })
}

export function getServerOk() {
  return api.get('/server/ok')
}

// --- Instance Management (JWT-protected manager endpoints) ---

export function getAllInstances() {
  return api.get('/manager-api/instances')
}

export function createInstance(data: CreateInstanceRequest) {
  return api.post('/manager-api/instances', data)
}

export function deleteInstance(id: string) {
  return api.delete(`/manager-api/instances/${id}`)
}

export function getInstanceInfo(id: string) {
  return api.get(`/manager-api/instances/${id}`)
}

export function getInstanceLogs(id: string, params?: { lines?: number; limit?: number; level?: string }) {
  return api.get(`/manager-api/instances/${id}/logs`, { params })
}

export function rotateToken(id: string) {
  return api.post(`/manager-api/instances/${id}/rotate-token`)
}

// --- v0.51 RyzeAPI parity: multi-slot webhooks por instância (até 3 slots) ---

export interface InstanceWebhookSlot {
  id?: string
  instanceId?: string
  slot: number
  url: string
  secret?: string
  events?: string[]
  enabled?: boolean
  headers?: Record<string, string> | string
  createdAt?: string
  updatedAt?: string
}

export function listInstanceWebhooks(id: string) {
  return api.get(`/manager-api/instances/${id}/webhooks`)
}

export function upsertInstanceWebhook(id: string, slot: number, data: Omit<InstanceWebhookSlot, 'slot' | 'instanceId' | 'id'>) {
  return api.put(`/manager-api/instances/${id}/webhooks/${slot}`, data)
}

export function deleteInstanceWebhook(id: string, slot: number) {
  return api.delete(`/manager-api/instances/${id}/webhooks/${slot}`)
}

export function setProxy(id: string, data: ProxyRequest) {
  return api.post(`/manager-api/instances/${id}/proxy`, data)
}

export function deleteProxy(id: string) {
  return api.delete(`/manager-api/instances/${id}/proxy`)
}

export function forceReconnect(id: string) {
  return api.post(`/manager-api/instances/${id}/forcereconnect`)
}

// --- Instance Operations (use instance token) ---

export function connectInstance(apikey: string, data: InstanceConnectionSettings = {}) {
  return api.post('/instance/connect', data, withInstanceKey(apikey))
}

export function getQrCode(apikey: string) {
  return api.get('/instance/qr', withInstanceKey(apikey))
}

export function pairInstance(apikey: string, number: string) {
  return api.post('/instance/pair', { number }, withInstanceKey(apikey))
}

export function disconnectInstance(apikey: string) {
  return api.post('/instance/disconnect', {}, withInstanceKey(apikey))
}

export function reconnectInstance(apikey: string) {
  return api.post('/instance/reconnect', {}, withInstanceKey(apikey))
}

export function logoutInstance(apikey: string) {
  return api.delete('/instance/logout', withInstanceKey(apikey))
}

export function getInstanceStatus(apikey: string) {
  return api.get('/instance/status', withInstanceKey(apikey))
}

export function getAdvancedSettings(id: string, apikey: string) {
  return api.get(`/instance/${id}/advanced-settings`, withInstanceKey(apikey))
}

export function updateAdvancedSettings(id: string, apikey: string, data: AdvancedSettings) {
  return api.put(`/instance/${id}/advanced-settings`, data, withInstanceKey(apikey))
}

// --- Send Messages (use instance token) ---

export function sendText(apikey: string, data: SendTextRequest) {
  return api.post('/send/text', data, withInstanceKey(apikey))
}

export function sendMedia(apikey: string, data: SendMediaRequest) {
  return api.post('/send/media', data, withInstanceKey(apikey))
}

export function sendLink(apikey: string, data: { number: string; url: string; title?: string; description?: string; imgUrl?: string; text?: string }) {
  return api.post('/send/link', data, withInstanceKey(apikey))
}

export function sendPoll(apikey: string, data: { number: string; question: string; options: string[]; maxAnswer?: number }) {
  return api.post('/send/poll', data, withInstanceKey(apikey))
}

export function sendSticker(
  apikey: string,
  data: { number: string; sticker: string; forwarded?: boolean },
) {
  return api.post('/send/sticker', data, withInstanceKey(apikey))
}

export function sendLocation(apikey: string, data: { number: string; latitude: number; longitude: number; name?: string; address?: string }) {
  return api.post('/send/location', data, withInstanceKey(apikey))
}

export function sendContact(apikey: string, data: { number: string; vcard?: { fullName: string; phoneNumber: string }; contacts?: { fullName: string; phoneNumber: string }[] }) {
  return api.post('/send/contact', data, withInstanceKey(apikey))
}

export function sendButton(apikey: string, data: Record<string, unknown>) {
  return api.post('/send/button', data, withInstanceKey(apikey))
}

export function sendList(apikey: string, data: Record<string, unknown>) {
  return api.post('/send/list', data, withInstanceKey(apikey))
}

export function sendPix(apikey: string, data: { number: string; merchantName: string; pixKey: string; keyType: string; headerTitle?: string; bodyText?: string; footerText?: string }) {
  return api.post('/send/pix', data, withInstanceKey(apikey))
}

export function sendBatchText(apikey: string, data: { numbers: string[]; text: string; delay?: number }) {
  return api.post('/send/batch/text', data, withInstanceKey(apikey))
}

// --- Communities (use instance token) ---

export function createCommunity(apikey: string, data: { communityName: string }) {
  return api.post('/community/create', data, withInstanceKey(apikey))
}

export function getCommunityInfo(apikey: string, data: { communityJid: string }) {
  return api.post('/community/info', data, withInstanceKey(apikey))
}

export function getCommunitySubgroups(apikey: string, data: { communityJid: string }) {
  return api.post('/community/subgroups', data, withInstanceKey(apikey))
}

export function addToCommunity(apikey: string, data: { communityJid: string; groupJid: string[] }) {
  return api.post('/community/add', data, withInstanceKey(apikey))
}

export function removeFromCommunity(apikey: string, data: { communityJid: string; groupJid: string[] }) {
  return api.post('/community/remove', data, withInstanceKey(apikey))
}

// --- Failover (Admin endpoints — uses global key from interceptor) ---

export function getFailoverGroups() {
  return api.get('/admin/failover/groups')
}

export function createFailoverGroup(data: FailoverGroupRequest) {
  return api.post('/admin/failover/groups', data)
}

export function getFailoverGroup(id: string) {
  return api.get(`/admin/failover/groups/${id}`)
}

export function deleteFailoverGroup(id: string) {
  return api.delete(`/admin/failover/groups/${id}`)
}

export function addFailoverMember(groupId: string, data: FailoverMemberRequest) {
  return api.post(`/admin/failover/groups/${groupId}/members`, data)
}

export function removeFailoverMember(instanceId: string) {
  return api.delete(`/admin/failover/members/${instanceId}`)
}

export function selectFailoverInstance(groupId: string) {
  return api.get(`/admin/failover/select/${groupId}`)
}

// --- Alerting (Admin endpoints) ---

export function getAlertStatus() {
  return api.get('/admin/alerts/status')
}

export function testAlert() {
  return api.post('/admin/alerts/test')
}

// --- Chat & Messages (use instance token) ---

export function getChatList(apikey: string) {
  return api.get('/chat/list', withInstanceKey(apikey))
}

export function getChatMessages(apikey: string, chatJid: string, params?: { limit?: number; offset?: number }) {
  return api.get(`/chat/messages/${encodeURIComponent(chatJid)}`, { ...withInstanceKey(apikey), params })
}

export function searchMessages(apikey: string, data: { query: string; limit?: number; offset?: number }) {
  return api.post('/chat/search', data, withInstanceKey(apikey))
}

// v0.67 — advanced message search (chat_jid, sender_jid, date range).
// Backend: POST /chat/search/advanced (already in routes.go since v0.32).
export interface MessageSearchAdvancedFilters {
  query?: string
  chat_jid?: string
  sender_jid?: string
  date_from?: string // ISO 8601
  date_to?: string   // ISO 8601
  limit?: number
  offset?: number
}
export function searchMessagesAdvanced(apikey: string, data: MessageSearchAdvancedFilters) {
  return api.post('/chat/search/advanced', data, withInstanceKey(apikey))
}

export function getRevokedMessages(apikey: string, params?: { limit?: number; offset?: number }) {
  return api.get('/message/revoked', { ...withInstanceKey(apikey), params })
}

// Per-message actions on the WhatsApp side (instance token)
/**
 * @deprecated The backend `/send/forward` endpoint cannot forward media —
 * it only re-sends a text message and requires a non-empty `text` field
 * (returns 400 otherwise). The Manager forward flow now routes client-side
 * via `useForwardBatch` → `sendText` / `sendMedia` / `sendSticker` with
 * `forwarded: true` set so the backend tags ContextInfo.IsForwarded.
 * Kept here for backward compatibility with external callers.
 */
export function forwardMessage(
  apikey: string,
  data: { number: string; messageId: string; participantJid?: string; chatJid?: string; text?: string },
) {
  return api.post('/send/forward', data, withInstanceKey(apikey))
}
export function editMessage(
  apikey: string,
  data: { number: string; messageId: string; text: string },
) {
  return api.post('/message/edit', data, withInstanceKey(apikey))
}
export function reactMessage(
  apikey: string,
  // Matches backend ReactStruct: number, reaction, id, fromMe, optional participant.
  // `reaction: ""` (or "remove") clears an existing reaction.
  data: { number: string; reaction: string; id: string; fromMe: boolean; participant?: string },
) {
  return api.post('/message/react', data, withInstanceKey(apikey))
}
export function deleteMessageForEveryone(
  apikey: string,
  data: { number: string; messageId: string },
) {
  return api.post('/message/delete', data, withInstanceKey(apikey))
}
export function getPollResults(apikey: string, messageId: string) {
  return api.get(`/polls/${encodeURIComponent(messageId)}/results`, withInstanceKey(apikey))
}

// --- Chatwoot (global config) ---

export function getChatwootConfig() {
  return api.get('/chatwoot/config')
}

// --- Chatwoot (per-instance config, uses instance token) ---

export interface ChatwootInstanceConfigInput {
  baseURL: string
  apiToken?: string
  accountID: number
  inboxID: number
  enabled: boolean
  signMsg?: boolean
  reopenConv?: boolean
  importContacts?: boolean
  forwardGroups?: boolean
}

export interface ChatwootInstanceConfigResponse {
  baseURL: string
  accountID: number
  inboxID: number
  enabled: boolean
  signMsg?: boolean
  reopenConv?: boolean
  importContacts?: boolean
  forwardGroups?: boolean
  webhookURL: string
  apiTokenMasked: string
}

export interface ChatwootInstanceTestResponse {
  ok: boolean
  inboxName?: string
  error?: string
}

export function getInstanceChatwootConfig(apikey: string) {
  return api.get('/instance/chatwoot/config', withInstanceKey(apikey))
}

export function saveInstanceChatwootConfig(apikey: string, data: ChatwootInstanceConfigInput) {
  return api.post('/instance/chatwoot/config', data, withInstanceKey(apikey))
}

export function deleteInstanceChatwootConfig(apikey: string) {
  return api.delete('/instance/chatwoot/config', withInstanceKey(apikey))
}

export function testInstanceChatwootConfig(apikey: string) {
  return api.post('/instance/chatwoot/test', {}, withInstanceKey(apikey))
}

// --- VoIP (use instance token) ---

export function getActiveCalls(apikey: string) {
  return api.get('/voip/call/active', withInstanceKey(apikey))
}

export function startCall(apikey: string, data: StartCallRequest) {
  return api.post('/voip/call/start', data, withInstanceKey(apikey))
}

export function endCall(apikey: string, data: EndCallRequest) {
  return api.post('/voip/call/end', data, withInstanceKey(apikey))
}

// --- Groups (use instance token) ---

export function getGroups(apikey: string) {
  return api.get('/group/list', withInstanceKey(apikey))
}

export function getMyGroups(apikey: string) {
  return api.get('/group/myall', withInstanceKey(apikey))
}

export function getGroupInfo(apikey: string, data: { groupJid: string }) {
  return api.post('/group/info', data, withInstanceKey(apikey))
}

export function getGroupInviteLink(apikey: string, data: { groupJid: string }) {
  return api.post('/group/invitelink', data, withInstanceKey(apikey))
}

export function getGroupInviteInfo(apikey: string, data: { code: string }) {
  return api.post('/group/invite-info', data, withInstanceKey(apikey))
}

export function joinGroup(apikey: string, data: { code: string }) {
  return api.post('/group/join', data, withInstanceKey(apikey))
}

export function getGroupJoinRequests(apikey: string, data: { groupJid: string }) {
  return api.post('/group/requests', data, withInstanceKey(apikey))
}

export function actionGroupJoinRequests(
  apikey: string,
  data: { groupJid: string; participants: string[]; action: 'approve' | 'reject' },
) {
  return api.post('/group/requests/action', data, withInstanceKey(apikey))
}

export function createGroup(apikey: string, data: { name: string; participants: string[] }) {
  return api.post('/group/create', data, withInstanceKey(apikey))
}

export function updateGroupParticipant(apikey: string, data: { groupJid: string; participants: string[]; action: string }) {
  return api.post('/group/participant', data, withInstanceKey(apikey))
}

export function setGroupName(apikey: string, data: { groupJid: string; name: string }) {
  return api.post('/group/name', data, withInstanceKey(apikey))
}

export function setGroupDescription(apikey: string, data: { groupJid: string; description: string }) {
  return api.post('/group/description', data, withInstanceKey(apikey))
}

export function setGroupPhoto(apikey: string, data: { groupJid: string; image: string }) {
  return api.post('/group/photo', data, withInstanceKey(apikey))
}

export function joinGroupLink(apikey: string, data: { link: string }) {
  return api.post('/group/join', data, withInstanceKey(apikey))
}

export function leaveGroup(apikey: string, data: { groupJid: string }) {
  return api.post('/group/leave', data, withInstanceKey(apikey))
}

export function setGroupSettings(apikey: string, data: { groupJid: string; edit?: string; announce?: string }) {
  return api.post('/group/settings', data, withInstanceKey(apikey))
}

// --- Contacts & User (use instance token) ---

export function getContacts(apikey: string) {
  return api.get('/user/contacts', withInstanceKey(apikey))
}

export function getUserInfo(apikey: string, data: { number: string }) {
  return api.post('/user/info', data, withInstanceKey(apikey))
}

export function checkUser(apikey: string, data: { number: string | string[] }) {
  const payload = {
    number: Array.isArray(data.number) ? data.number : [data.number]
  }
  return api.post('/user/check', payload, withInstanceKey(apikey))
}

export function getUserAvatar(apikey: string, data: { number: string }) {
  return api.post('/user/avatar', data, withInstanceKey(apikey))
}

export function blockContact(apikey: string, data: { number: string }) {
  return api.post('/user/block', data, withInstanceKey(apikey))
}

export function unblockContact(apikey: string, data: { number: string }) {
  return api.post('/user/unblock', data, withInstanceKey(apikey))
}

export function getBlockList(apikey: string) {
  return api.get('/user/blocklist', withInstanceKey(apikey))
}

export function getPrivacy(apikey: string) {
  return api.get('/user/privacy', withInstanceKey(apikey))
}

export function setPrivacy(apikey: string, data: Record<string, unknown>) {
  return api.post('/user/privacy', data, withInstanceKey(apikey))
}

export function setProfilePicture(apikey: string, data: { image: string }) {
  return api.post('/user/profilePicture', data, withInstanceKey(apikey))
}

export function setProfileName(apikey: string, data: { name: string }) {
  return api.post('/user/profileName', data, withInstanceKey(apikey))
}

export function setProfileStatus(apikey: string, data: { status: string }) {
  return api.post('/user/profileStatus', data, withInstanceKey(apikey))
}

// --- Labels (use instance token) ---

export function getLabels(apikey: string) {
  return api.get('/label/list', withInstanceKey(apikey))
}

export function editLabel(apikey: string, data: { labelId: string; name?: string; color?: number; deleted?: boolean }) {
  return api.post('/label/edit', data, withInstanceKey(apikey))
}

export function labelChat(apikey: string, data: { chatJid: string; labelId: string }) {
  return api.post('/label/chat', data, withInstanceKey(apikey))
}

export function labelMessage(apikey: string, data: { chatJid: string; messageId: string; labelId: string }) {
  return api.post('/label/message', data, withInstanceKey(apikey))
}

export function unlabelChat(apikey: string, data: { chatJid: string; labelId: string }) {
  return api.post('/unlabel/chat', data, withInstanceKey(apikey))
}

export function unlabelMessage(apikey: string, data: { chatJid: string; messageId: string; labelId: string }) {
  return api.post('/unlabel/message', data, withInstanceKey(apikey))
}

// --- Newsletter (use instance token) ---

export function getNewsletters(apikey: string) {
  return api.get('/newsletter/list', withInstanceKey(apikey))
}

// Backend expects { jid } (whatsmeow types.JID); frontend uses newsletterId for
// clarity. Translate at the boundary so callers don't need to know the JSON key.
export function getNewsletterInfo(apikey: string, data: { newsletterId: string }) {
  return api.post('/newsletter/info', { jid: data.newsletterId }, withInstanceKey(apikey))
}

export function createNewsletter(apikey: string, data: { name: string; description?: string }) {
  return api.post('/newsletter/create', data, withInstanceKey(apikey))
}

export function subscribeNewsletter(apikey: string, data: { newsletterId: string }) {
  return api.post('/newsletter/subscribe', { jid: data.newsletterId }, withInstanceKey(apikey))
}

export function getNewsletterMessages(apikey: string, data: { newsletterId: string; count?: number }) {
  return api.post(
    '/newsletter/messages',
    { jid: data.newsletterId, count: data.count ?? 0 },
    withInstanceKey(apikey),
  )
}

export function renameInstance(id: string, name: string) {
  return api.patch(`/manager-api/instances/${id}/rename`, { name })
}

export function getWebhookDeadLetters(instanceId: string, params?: { limit?: number }) {
  return api.get(`/manager-api/instances/${instanceId}/webhook-dead-letters`, { params })
}

export function retryWebhookDeadLetter(id: number) {
  return api.post(`/manager-api/webhook-dead-letters/${id}/retry`)
}

// --- Auth (JWT) ---

export function authRegister(data: { companyName: string; name: string; email: string; password: string }) {
  return api.post('/auth/register', data)
}

export function authLogin(data: { email: string; password: string }) {
  return api.post('/auth/login', data)
}

export function authRefresh(data: { refreshToken: string }) {
  return api.post('/auth/refresh', data)
}

export function authMe() {
  return api.get('/auth/me')
}

export function authUpdateProfile(data: { name?: string; avatar?: string }) {
  return api.patch('/auth/me', data)
}

export function authChangePassword(data: { currentPassword: string; newPassword: string }) {
  return api.post('/auth/me/password', data)
}

// --- SSO (Keycloak / Microsoft 365) ---

export const getSSOConfig = () => api.get('/auth/sso/config')
export const getSSOAuthorizeURL = () => api.get('/auth/sso/authorize')

// --- Organization & Users ---

export function getOrganization() {
  return api.get('/manager-api/org')
}

export function updateOrganization(data: Record<string, unknown>) {
  return api.patch('/manager-api/org', data)
}

export function getUsers() {
  return api.get('/manager-api/users')
}

export function inviteUser(data: { email: string; name: string; roleId: string }) {
  return api.post('/manager-api/users/invite', data)
}

export function listInvitations() {
  return api.get('/manager-api/invitations')
}

export function reissueInvitation(userId: string) {
  return api.post(`/manager-api/users/${userId}/invitation/reissue`)
}

export function revokeInvitation(inviteId: string) {
  return api.post(`/manager-api/invitations/${inviteId}/revoke`)
}

export function updateUserRole(userId: string, roleId: string) {
  return api.patch(`/manager-api/users/${userId}/role`, { roleId })
}

// Pin (or unpin via empty array) a user to specific instances.
// Pass [] to clear and grant full org access. Takes effect on next token refresh.
export function setUserAllowedInstances(userId: string, allowedInstanceIds: string[]) {
  return api.patch(`/manager-api/users/${userId}/allowed-instances`, { allowedInstanceIds })
}

export function deactivateUser(userId: string) {
  return api.delete(`/manager-api/users/${userId}`)
}

// --- Roles ---

export function getRoles() {
  return api.get('/manager-api/roles')
}

export function createRole(data: Record<string, unknown>) {
  return api.post('/manager-api/roles', data)
}

export function updateRole(id: string, data: Record<string, unknown>) {
  return api.patch(`/manager-api/roles/${id}`, data)
}

export function deleteRole(id: string) {
  return api.delete(`/manager-api/roles/${id}`)
}

export function getPermissions() {
  return api.get('/manager-api/permissions')
}

// --- Teams ---

export function getTeams() {
  return api.get('/manager-api/teams')
}

export function createTeam(data: {
  name: string
  description?: string
  maskContactPhones?: boolean
  allowedInstanceIds?: string[]
}) {
  return api.post('/manager-api/teams', data)
}

export function updateTeam(
  id: string,
  data: { name?: string; description?: string; maskContactPhones?: boolean; allowedInstanceIds?: string[] },
) {
  return api.patch(`/manager-api/teams/${id}`, data)
}

export function deleteTeam(id: string) {
  return api.delete(`/manager-api/teams/${id}`)
}

export function addTeamMember(teamId: string, data: { userId: string; role?: string }) {
  return api.post(`/manager-api/teams/${teamId}/members`, data)
}

export function removeTeamMember(teamId: string, userId: string) {
  return api.delete(`/manager-api/teams/${teamId}/members/${userId}`)
}

// --- Inbox ---
export function getConversations(params?: Record<string, unknown>) {
  return api.get('/manager-api/inbox/conversations', { params })
}
export function getConversation(id: string) {
  return api.get(`/manager-api/inbox/conversations/${id}`)
}
export function updateConversation(id: string, data: Record<string, unknown>) {
  return api.patch(`/manager-api/inbox/conversations/${id}`, data)
}
export function resolveConversation(id: string) {
  return api.post(`/manager-api/inbox/conversations/${id}/resolve`)
}
export function snoozeConversation(id: string, data: { until: string }) {
  return api.post(`/manager-api/inbox/conversations/${id}/snooze`, data)
}
export function transferConversation(id: string, data: Record<string, unknown>) {
  return api.post(`/manager-api/inbox/conversations/${id}/transfer`, data)
}
export function inboxReply(data: {
  conversationId: string
  text: string
  quotedId?: string
  quotedText?: string
  quotedFromMe?: boolean
  quotedParticipant?: string
}) {
  return api.post('/manager-api/inbox/reply', data)
}
export function inboxReplyMedia(form: FormData) {
  return api.post('/manager-api/inbox/reply-media', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}
export function inboxReact(
  conversationId: string,
  messageId: string,
  data: { emoji: string; fromMe?: boolean; participant?: string },
) {
  return api.post(
    `/manager-api/inbox/conversations/${conversationId}/messages/${encodeURIComponent(messageId)}/react`,
    data,
  )
}
export function inboxAssignMe(conversationId: string) {
  return api.post(`/manager-api/inbox/conversations/${conversationId}/assign-me`)
}
// Right-rail bundle: conversation + contact (with company) + previous convos +
// notes + macros + custom-field defs/values in a single round-trip.
export function getConversationRelated(conversationId: string) {
  return api.get(`/manager-api/conversations/${conversationId}/related`)
}
export function getUnreadCount() {
  return api.get('/manager-api/inbox/unread-count')
}

// --- CRM Contacts ---
export function getContacts2(params?: Record<string, unknown>) {
  return api.get('/manager-api/contacts', { params })
}
export function createContact(data: Record<string, unknown>) {
  return api.post('/manager-api/contacts', data)
}
export function getContact(id: string) {
  return api.get(`/manager-api/contacts/${id}`)
}
export function updateContact(id: string, data: Record<string, unknown>) {
  return api.patch(`/manager-api/contacts/${id}`, data)
}
export function deleteContact(id: string) {
  return api.delete(`/manager-api/contacts/${id}`)
}
export function importContacts(data: Record<string, unknown>[]) {
  return api.post('/manager-api/contacts/import', { contacts: data })
}
export function exportContacts() {
  return api.get('/manager-api/contacts/export')
}

// v0.74.x — Contact profile sync (WhatsApp name/avatar with manual override + history).
// All five endpoints share the same auth as PATCH /contacts/:id (PermContactWrite,
// except photo-history which only needs PermContactRead).
export function getContactPhotoHistory(contactId: string) {
  return api.get(`/manager-api/contacts/${contactId}/photo-history`)
}
export function refreshContactAvatar(contactId: string) {
  return api.post(`/manager-api/contacts/${contactId}/refresh-avatar`)
}
export function uploadContactAvatar(contactId: string, file: File) {
  const formData = new FormData()
  formData.append('avatar', file)
  return api.post(`/manager-api/contacts/${contactId}/avatar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}
export function clearManualContactAvatar(contactId: string) {
  return api.delete(`/manager-api/contacts/${contactId}/avatar`)
}
export function clearManualContactName(contactId: string) {
  return api.delete(`/manager-api/contacts/${contactId}/name`)
}

// v0.75.x — Bulk avatar backfill. Asynchronous job + status polling.
// Triggered from ContactsView header for backfilling 13k contacts without
// avatars after migration.
export interface BulkAvatarSyncOpts {
  instanceId?: string
  onlyMissing?: boolean
  maxConcurrent?: number
  ratePerSec?: number
}

export interface BulkAvatarSyncStatus {
  jobId: string
  orgId: string
  startedAt: string
  finishedAt?: string | null
  total: number
  done: number
  skipped: number
  errors: number
  lastError?: string
  opts: {
    onlyMissing: boolean
    instanceId: string
    maxConcurrent: number
    ratePerSec: number
  }
}

export function bulkSyncAvatars(opts: BulkAvatarSyncOpts = {}) {
  return api.post<{ jobId: string }>('/manager-api/contacts/bulk-sync-avatars', opts)
}

export function bulkSyncAvatarStatus(jobId: string) {
  return api.get<BulkAvatarSyncStatus>(`/manager-api/contacts/bulk-sync-avatars/${jobId}`)
}

// --- Companies ---
export function getCompanies(params?: Record<string, unknown>) {
  return api.get('/manager-api/companies', { params })
}
export function getCompany(id: string) {
  return api.get(`/manager-api/companies/${id}`)
}
export function createCompany(data: Record<string, unknown>) {
  return api.post('/manager-api/companies', data)
}
export function updateCompany(id: string, data: Record<string, unknown>) {
  return api.patch(`/manager-api/companies/${id}`, data)
}
export function deleteCompany(id: string) {
  return api.delete(`/manager-api/companies/${id}`)
}

// --- Canned Responses ---
export function getCannedResponses(params?: Record<string, unknown>) {
  return api.get('/manager-api/canned-responses', { params })
}
export function createCannedResponse(data: Record<string, unknown>) {
  return api.post('/manager-api/canned-responses', data)
}
export function updateCannedResponse(id: string, data: Record<string, unknown>) {
  return api.patch(`/manager-api/canned-responses/${id}`, data)
}
export function deleteCannedResponse(id: string) {
  return api.delete(`/manager-api/canned-responses/${id}`)
}
export function resolveCannedShortcode(shortcode: string) {
  return api.post('/manager-api/canned-responses/resolve', { shortcode })
}

// --- Analytics ---
export function getAnalyticsDashboard(params?: Record<string, unknown>) {
  return api.get('/manager-api/analytics/dashboard', { params })
}
export function getAnalyticsOverview() {
  return api.get('/manager-api/analytics/overview')
}
export function getAnalyticsAgents(params?: Record<string, unknown>) {
  return api.get('/manager-api/analytics/agents', { params })
}
export function getAnalyticsTrends(params?: Record<string, unknown>) {
  return api.get('/manager-api/analytics/trends', { params })
}

// --- Automations ---
export function getAutomations(params?: Record<string, unknown>) {
  return api.get('/manager-api/automations', { params })
}
export function createAutomation(data: Record<string, unknown>) {
  return api.post('/manager-api/automations', data)
}
export function getAutomation(id: string) {
  return api.get(`/manager-api/automations/${id}`)
}
export function updateAutomation(id: string, data: Record<string, unknown>) {
  return api.patch(`/manager-api/automations/${id}`, data)
}
export function deleteAutomation(id: string) {
  return api.delete(`/manager-api/automations/${id}`)
}
export function toggleAutomation(id: string) {
  return api.post(`/manager-api/automations/${id}/toggle`)
}

// --- Integrations ---
export function getIntegrations() {
  return api.get('/manager-api/integrations')
}
export function createIntegration(data: Record<string, unknown>) {
  return api.post('/manager-api/integrations', data)
}
export function getIntegration(id: string) {
  return api.get(`/manager-api/integrations/${id}`)
}
export function updateIntegration(id: string, data: Record<string, unknown>) {
  return api.patch(`/manager-api/integrations/${id}`, data)
}
export function deleteIntegration(id: string) {
  return api.delete(`/manager-api/integrations/${id}`)
}
export function testIntegration(id: string) {
  return api.post(`/manager-api/integrations/${id}/test`)
}
export function getIntegrationTypes() {
  return api.get('/manager-api/integrations/types')
}

// --- Notifications ---
export function getNotifications(params?: Record<string, unknown>) {
  return api.get('/manager-api/notifications', { params })
}
export function getNotificationUnreadCount() {
  return api.get('/manager-api/notifications/unread-count')
}
export function markNotificationsRead(ids: string[]) {
  return api.post('/manager-api/notifications/mark-read', { ids })
}
export function markAllNotificationsRead() {
  return api.post('/manager-api/notifications/mark-all-read')
}

// --- Starred Messages (WhatsApp-style "Mensagens favoritas") ---
//
// Star/unstar are scoped to the authenticated operator via the JWT — no need
// to send userId. The bubble caller passes instanceId + conversationId so the
// server can denormalize them onto the row without an extra join.
export function starMessage(
  messageId: string,
  data: { instanceId: string; conversationId?: string },
) {
  return api.post(`/manager-api/messages/${messageId}/star`, data)
}
export function unstarMessage(messageId: string) {
  return api.delete(`/manager-api/messages/${messageId}/star`)
}
export function listStarredMessages(params?: { limit?: number; offset?: number }) {
  return api.get('/manager-api/messages/starred', { params })
}
export function listConversationStarredMessages(conversationId: string) {
  return api.get(`/manager-api/conversations/${conversationId}/starred-messages`)
}

// --- Conversation Notes ---
export function getConversationNotes(conversationId: string) {
  return api.get(`/manager-api/conversations/${conversationId}/notes`)
}
export function createConversationNote(conversationId: string, data: { content: string }) {
  return api.post(`/manager-api/conversations/${conversationId}/notes`, data)
}
export function updateConversationNote(noteId: string, data: { content: string }) {
  return api.patch(`/manager-api/notes/${noteId}`, data)
}
export function deleteConversationNote(noteId: string) {
  return api.delete(`/manager-api/notes/${noteId}`)
}

// --- Inbox Enhancement ---
export function getConversationDetail(id: string, params?: Record<string, unknown>) {
  return api.get(`/manager-api/inbox/conversations/${id}/detail`, { params })
}
export function getInboxInstances() {
  return api.get('/manager-api/inbox/instances')
}
export function syncInboxConversations() {
  return api.post('/manager-api/inbox/sync')
}

// --- Custom Fields ---
export function getCustomFieldDefinitions(params?: Record<string, unknown>) {
  return api.get('/manager-api/custom-fields', { params })
}
export function createCustomFieldDefinition(data: Record<string, unknown>) {
  return api.post('/manager-api/custom-fields', data)
}
export function updateCustomFieldDefinition(id: string, data: Record<string, unknown>) {
  return api.patch(`/manager-api/custom-fields/${id}`, data)
}
export function deleteCustomFieldDefinition(id: string) {
  return api.delete(`/manager-api/custom-fields/${id}`)
}
export function getCustomFieldValues(entityId: string) {
  return api.get(`/manager-api/custom-fields/values/${entityId}`)
}
export function setCustomFieldValue(data: Record<string, unknown>) {
  return api.post('/manager-api/custom-fields/values', data)
}

// --- Macros ---
export function getMacros() {
  return api.get('/manager-api/macros')
}
export function createMacro(data: Record<string, unknown>) {
  return api.post('/manager-api/macros', data)
}
export function getMacro(id: string) {
  return api.get(`/manager-api/macros/${id}`)
}
export function updateMacro(id: string, data: Record<string, unknown>) {
  return api.patch(`/manager-api/macros/${id}`, data)
}
export function deleteMacro(id: string) {
  return api.delete(`/manager-api/macros/${id}`)
}
export function executeMacro(id: string, data: { conversationId: string }) {
  return api.post(`/manager-api/macros/${id}/execute`, data)
}

// --- Push Notifications ---
export function pushSubscribe(data: { endpoint: string; keys: { p256dh: string; auth: string } }) {
  return api.post('/manager-api/push/subscribe', data)
}
export function pushUnsubscribe(data: { endpoint: string }) {
  return api.post('/manager-api/push/unsubscribe', data)
}
export function getVAPIDKey() {
  return api.get('/manager-api/push/vapid-key')
}
export function getPushStatus() {
  return api.get('/manager-api/push/status')
}

// --- Campaigns ---
export function getCampaigns() { return api.get('/manager-api/campaigns') }
export function getCampaignStats() { return api.get('/manager-api/campaigns/stats') }
export function getCampaign(id: string) { return api.get(`/manager-api/campaigns/${id}`) }
export function createCampaign(data: Record<string, unknown>) { return api.post('/manager-api/campaigns', data) }
export function updateCampaign(id: string, data: Record<string, unknown>) { return api.patch(`/manager-api/campaigns/${id}`, data) }
export function deleteCampaign(id: string) { return api.delete(`/manager-api/campaigns/${id}`) }
export function startCampaign(id: string) { return api.post(`/manager-api/campaigns/${id}/start`) }
export function pauseCampaign(id: string) { return api.post(`/manager-api/campaigns/${id}/pause`) }
export function cancelCampaign(id: string) { return api.post(`/manager-api/campaigns/${id}/cancel`) }
export function resumeCampaign(id: string) { return api.post(`/manager-api/campaigns/${id}/resume`) }
export function duplicateCampaign(id: string) { return api.post(`/manager-api/campaigns/${id}/duplicate`) }
export function getCampaignLogs(id: string) { return api.get(`/manager-api/campaigns/${id}/logs`) }
export function getSSEStreamToken() { return api.post('/manager-api/campaigns/stream-token') }
export function paraphraseCampaign(messageContent: string) { return api.post('/manager-api/campaigns/paraphrase', { messageContent }) }
export function uploadCampaignMedia(file: File) {
  const formData = new FormData()
  formData.append('file', file)
  return api.post('/manager-api/campaigns/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}
export function listPhraseBlocks(params?: { category?: string; manage?: boolean }) { return api.get('/manager-api/campaigns/phrase-blocks', { params }) }
export function createPhraseBlock(data: { category: string; content: string }) { return api.post('/manager-api/campaigns/phrase-blocks', data) }
export function deletePhraseBlock(id: string) { return api.delete(`/manager-api/campaigns/phrase-blocks/${id}`) }
export function hidePhraseBlock(id: string) { return api.post(`/manager-api/campaigns/phrase-blocks/${id}/hide`) }
export function unhidePhraseBlock(id: string) { return api.post(`/manager-api/campaigns/phrase-blocks/${id}/unhide`) }
export function getUniqueTags() { return api.get('/manager-api/contacts/tags') }

// --- MCP Servers ---
export function getMCPServers() { return api.get('/manager-api/mcp-servers') }
export function createMCPServer(data: Record<string, unknown>) { return api.post('/manager-api/mcp-servers', data) }
export function getMCPServer(id: string) { return api.get(`/manager-api/mcp-servers/${id}`) }
export function updateMCPServer(id: string, data: Record<string, unknown>) { return api.patch(`/manager-api/mcp-servers/${id}`, data) }
export function deleteMCPServer(id: string) { return api.delete(`/manager-api/mcp-servers/${id}`) }
export function testMCPServer(id: string) { return api.post(`/manager-api/mcp-servers/${id}/test`) }
export function getMCPServerTools(id: string) { return api.get(`/manager-api/mcp-servers/${id}/tools`) }

// --- Platform Admin (Sys Admin only) ---
export function platformListOrganizations(params?: { page?: number; limit?: number }) {
  return api.get('/admin/platform/orgs', { params })
}
export function platformGetOrganization(id: string) {
  return api.get(`/admin/platform/orgs/${id}`)
}
export function platformCreateOrganization(data: {
  name: string
  slug: string
  plan?: string
  maxInstances?: number
  maxUsers?: number
}) {
  return api.post('/admin/platform/orgs', data)
}
export function platformUpdateOrganization(id: string, data: Record<string, unknown>) {
  return api.patch(`/admin/platform/orgs/${id}`, data)
}
// Hard-delete an organization. Backend requires the typed name to match the
// org's `name` field exactly (case-sensitive) so the dialog must validate
// client-side before this fires. Server still re-validates and returns
// 400 {"error":"confirmation mismatch"} on disagreement.
export function platformDeleteOrganization(id: string, confirm: string, mode: 'soft' | 'hard' = 'hard') {
  return api.delete(`/admin/platform/orgs/${id}`, {
    params: { mode, confirm },
  })
}

// Switches a PlatformAdmin's session into the target org without re-login.
// Caller must persist the new tokens via userStore.setTokens and reload
// the app so every subsequent request reads the new `org` claim.
export function platformImpersonateOrg(orgId: string, data: { reason: string; ticketId?: string }) {
  return api.post(`/admin/platform/orgs/${orgId}/impersonate`, data)
}

export function platformInviteOrgUser(orgId: string, data: { email: string; name: string; roleId: string }) {
  return api.post(`/admin/platform/orgs/${orgId}/users/invite`, data)
}

export function platformUpdateOrgUserRole(orgId: string, userId: string, roleId: string) {
  return api.patch(`/admin/platform/orgs/${orgId}/users/${userId}/role`, { roleId })
}

export function platformSetOrgUserAllowedInstances(orgId: string, userId: string, allowedInstanceIds: string[]) {
  return api.patch(`/admin/platform/orgs/${orgId}/users/${userId}/allowed-instances`, { allowedInstanceIds })
}

export function platformDeactivateOrgUser(orgId: string, userId: string) {
  return api.delete(`/admin/platform/orgs/${orgId}/users/${userId}`)
}

export function platformListOrgInvitations(orgId: string) {
  return api.get(`/admin/platform/orgs/${orgId}/invitations`)
}

export function platformReissueOrgUserInvitation(orgId: string, userId: string) {
  return api.post(`/admin/platform/orgs/${orgId}/users/${userId}/invitation/reissue`)
}

export function platformRevokeOrgInvitation(orgId: string, inviteId: string) {
  return api.post(`/admin/platform/orgs/${orgId}/invitations/${inviteId}/revoke`)
}
export function platformImpersonateUser(userId: string, data: { reason: string; ticketId?: string }) {
  return api.post(`/admin/platform/users/${userId}/impersonate`, data)
}
// Exit impersonation: regenerate a JWT pair scoped back to the actor's
// home org. Persist tokens + reload (same flow as impersonate).
export function platformExitImpersonation() {
  return api.post('/admin/platform/exit-impersonation')
}
export function platformListUsers(params?: { page?: number; limit?: number }) {
  return api.get('/admin/platform/users', { params })
}
export function platformSetPlatformAdmin(
  userId: string,
  isPlatformAdmin: boolean,
  platformRole?: string,
  reason?: string,
) {
  return api.patch(`/admin/platform/users/${userId}/platform-admin`, {
    isPlatformAdmin,
    platformRole: platformRole || '',
    reason: reason || '',
  })
}
// Phase 2 platform-role catalog (seeded in pkg/rbac/model/platform_permissions.go).
export function platformListPlatformRoles() {
  return api.get('/admin/platform/roles')
}
// Audit log filter contract — mirrors pkg/platform/model.ListAuditRequest.
// `actorEmail` is a case-insensitive substring (ILIKE) on the backend so
// the Manager filter box behaves like a search rather than a strict equals.
// `dateFrom`/`dateTo` accept either ISO-8601 or YYYY-MM-DD.
export interface PlatformAuditFilter {
  page?: number
  limit?: number
  action?: string
  actorUserId?: string
  actorEmail?: string
  targetType?: string
  targetId?: string
  dateFrom?: string
  dateTo?: string
}
export function platformListAudit(params?: PlatformAuditFilter) {
  return api.get('/admin/platform/audit', { params })
}
export interface PlatformSupportSessionFilter {
  page?: number
  limit?: number
  mode?: 'org' | 'user' | ''
  active?: 'true' | 'false' | ''
  actorEmail?: string
  targetEmail?: string
  targetOrgId?: string
  ticketId?: string
}
export function platformListSupportSessions(params?: PlatformSupportSessionFilter) {
  return api.get('/admin/platform/support-sessions', { params })
}
// CSV export — capped at 500 rows server-side. `X-Row-Count` response
// header tells callers whether the cap was hit.
export function platformExportAudit(params?: Omit<PlatformAuditFilter, 'page' | 'limit'>) {
  return api.get('/admin/platform/audit/export', {
    params,
    responseType: 'blob',
  })
}
export function platformListOrgFeatures(orgId: string) {
  return api.get(`/admin/platform/orgs/${orgId}/features`)
}

// Plan catalog (Platform Admin). Public read (login + signup) lives at
// /server/pricing — handled by the plans store separately.
export function platformListPlans() {
  return api.get('/admin/platform/plans')
}
export function platformCreatePlan(data: {
  slug: string
  name: string
  description?: string
  monthlyCents?: number
  maxInstances?: number
  maxUsers?: number
  features?: string[]
  isActive?: boolean
  isPublic?: boolean
  sortOrder?: number
}) {
  return api.post('/admin/platform/plans', data)
}
export function platformUpdatePlan(id: string, data: Record<string, unknown>) {
  return api.patch(`/admin/platform/plans/${id}`, data)
}
export function platformDeletePlan(id: string) {
  return api.delete(`/admin/platform/plans/${id}`)
}
export function platformSetOrgFeature(orgId: string, key: string, enabled: boolean) {
  return api.put(`/admin/platform/orgs/${orgId}/features/${key}`, { enabled })
}
export function platformDeleteOrgFeature(orgId: string, key: string) {
  return api.delete(`/admin/platform/orgs/${orgId}/features/${key}`)
}

// --- Platform Admin: Media Retention Policies ---
export function platformListRetention() {
  return api.get('/admin/platform/retention')
}
export function platformGetRetention(orgId: string) {
  return api.get(`/admin/platform/retention/${orgId}`)
}
export function platformUpdateRetention(orgId: string, data: {
  groupMediaDays?: number
  personalMediaDays?: number
  notes?: string
}) {
  return api.put(`/admin/platform/retention/${orgId}`, data)
}

// --- My Features (any authed user) ---
export function getMyFeatures() {
  return api.get('/auth/me/features')
}

// --- Release management ---
// Source of truth: CHANGELOG.md embedded into the binary. Backend parses on
// boot + upserts into the releases / release_entries tables. UI consumes
// the cached rows; Sync re-parses on demand (platform admin only).
export interface ReleaseEntry {
  id: string
  releaseId: string
  category: 'added' | 'changed' | 'fixed' | 'removed' | 'security' | 'deprecated'
  title: string
  description: string
  sortOrder: number
}
export interface Release {
  id: string
  version: string
  title: string
  summary: string
  commitSha: string
  dockerImage: string
  publishedAt: string
  gateStatus: 'passed' | 'failed' | 'unknown'
  gateCommand: string
  gateOutput: string
  isCurrent: boolean
  createdAt: string
  updatedAt: string
  entries: ReleaseEntry[]
}
export interface BuildInfo {
  version: string
  commit: string
  buildDate: string
  startedAt: string
}
export function listReleases(limit?: number) {
  return api.get<{ releases: Release[]; build: BuildInfo }>('/manager-api/releases', {
    params: { limit },
  })
}
export function getCurrentRelease() {
  return api.get<{ release: Release | null; build: BuildInfo }>('/manager-api/releases/current')
}
export function getRelease(version: string) {
  return api.get<Release>(`/manager-api/releases/${encodeURIComponent(version)}`)
}
export function syncReleases() {
  return api.post<{ synced: number }>('/admin/platform/releases/sync')
}

// --- Platform admin: instance metadata (v0.13/v0.14/v0.15) ---
// Source of truth: `instances.metadata jsonb` (migration v12). Endpoints
// are platform-admin gated; the Manager Admin UI uses them to inspect
// + edit free-form attributes ("user.id", "tenant.tier", region, etc.).
export interface AdminInstance {
  id: string
  name: string
  jid: string
  connected: boolean
  organizationId?: string | null
  metadata?: Record<string, unknown>
  bannedUntil?: string | null
  bannedReason?: string | null
}
export function platformListInstances() {
  return api.get<{ data: AdminInstance[]; count: number }>('/admin/platform/instances')
}
export function platformSearchInstancesByMetadata(filter: Record<string, unknown>) {
  return api.get<{ data: AdminInstance[]; count: number }>(
    '/admin/platform/instances/search',
    { params: { filter: JSON.stringify(filter) } },
  )
}
export function platformReplaceInstanceMetadata(instanceId: string, body: Record<string, unknown>) {
  return api.put(`/admin/platform/instances/${encodeURIComponent(instanceId)}/metadata`, body)
}
export function platformPatchInstanceMetadata(instanceId: string, patch: Record<string, unknown>) {
  return api.patch(`/admin/platform/instances/${encodeURIComponent(instanceId)}/metadata`, patch)
}

export interface InstanceMetricsSnapshot {
  instanceId: string
  name: string
  connected: boolean
  bannedUntil?: string | null
  bannedReason?: string
  sentLast24h: number
  receivedLast24h: number
  replyRatio24h: number
  organizationId?: string
  jid?: string
  createdAt: string
}
export function platformGetInstanceMetrics(instanceId: string) {
  return api.get<{ data: InstanceMetricsSnapshot }>(
    `/admin/platform/instances/${encodeURIComponent(instanceId)}/metrics`,
  )
}

// v0.66 — Story B (Manager UI completion).
//
// Tipos e helpers para as 4 surfaces sem UI até então: stats dashboard,
// bulk actions, test event, rate-limit snapshot. Todos usam o apikey
// global (rota `/admin/platform/*` autenticada por AuthAdmin).

export interface PlatformStats {
  instances: { total: number; connected: number; disconnected: number; bannedNow: number }
  messages:  { sentLast24h: number; receivedLast24h: number }
  scheduled: { pending: number; sent: number; failed: number; cancelled: number }
  webhooks:  { deadLetterCount: number }
}

export function getPlatformStats() {
  return api.get<{ data: PlatformStats }>('/admin/platform/stats')
}

export interface RateLimitSnapshotEntry {
  instanceId: string
  tokens: number
  refillIntervalMs: number
  circadianWindow: 'day' | 'night' | 'deep_night' | string
  consecutiveSends: number
  lastRefillAt: string
}

export function getPlatformRateLimits() {
  return api.get<{ data: RateLimitSnapshotEntry[] } | RateLimitSnapshotEntry[]>(
    '/admin/platform/rate-limits',
  )
}

export interface BulkActionRequest {
  instanceIds: string[]
  action: 'reconnect' | 'rotate_token' | 'patch_metadata'
  metadataPatch?: Record<string, unknown>
}
export interface BulkActionResultEntry {
  instanceId: string
  ok: boolean
  error?: string
}
export function platformBulkAction(req: BulkActionRequest) {
  return api.post<{ data: BulkActionResultEntry[] }>(
    '/admin/platform/instances/bulk/action',
    req,
  )
}

export interface TestEventRequest {
  eventName: string
  data?: Record<string, unknown>
}
export interface TestEventResult {
  delivered: boolean
  statusCode: number
  durationMs: number
  signature?: string
  error?: string
}
export function platformTriggerTestEvent(instanceId: string, req: TestEventRequest) {
  return api.post<{ data: TestEventResult }>(
    `/admin/platform/instances/${encodeURIComponent(instanceId)}/events/test`,
    req,
  )
}

// --- Group Config (manager-api, JWT-protected) ---

export interface GroupConfig {
  id?: string
  organizationId?: string
  groupJid: string
  welcomeEnabled: boolean
  welcomeType: 'inline' | 'private' | 'both'
  welcomeMessage: string
  spamShieldEnabled: boolean
  privateTransitionEnabled: boolean
  privateTransitionMessage: string
  createdAt?: string
  updatedAt?: string
}

export interface SaveGroupConfigInput {
  welcomeEnabled: boolean
  welcomeType: 'inline' | 'private' | 'both'
  welcomeMessage: string
  spamShieldEnabled: boolean
  privateTransitionEnabled: boolean
  privateTransitionMessage: string
}

export function getGroupConfig(groupJid: string) {
  return api.get<GroupConfig>(`/manager-api/groups/${encodeURIComponent(groupJid)}/config`)
}

export function saveGroupConfig(groupJid: string, data: SaveGroupConfigInput) {
  return api.post<GroupConfig>(`/manager-api/groups/${encodeURIComponent(groupJid)}/config`, data)
}

// --- Blacklist (manager-api, JWT-protected) ---

export interface BlacklistWord {
  id: string
  organizationId: string
  word: string
  createdAt: string
  updatedAt: string
}

export function getBlacklist() {
  return api.get<BlacklistWord[]>('/manager-api/blacklist')
}

export function addBlacklistWord(word: string) {
  return api.post<BlacklistWord>('/manager-api/blacklist', { word })
}

export function deleteBlacklistWord(id: string) {
  return api.delete(`/manager-api/blacklist/${encodeURIComponent(id)}`)
}

// --- Rotators (manager-api, JWT-protected) ---

export interface RotatorTarget {
  id?: string
  rotatorId?: string
  jid: string
  url: string
  cap: number
  currentClicks?: number
  priority: number
  status: 'active' | 'paused' | 'exhausted'
  createdAt?: string
  updatedAt?: string
}

export interface Rotator {
  id?: string
  organizationId?: string
  name: string
  slug: string
  description: string
  targets: RotatorTarget[]
  createdAt?: string
  updatedAt?: string
}

export interface CreateRotatorInput {
  name: string
  slug: string
  description: string
  targets: Array<{
    jid: string
    url: string
    cap: number
    priority: number
    status: string
  }>
}

export function getRotators() {
  return api.get<Rotator[]>('/manager-api/rotators')
}

export function getRotator(id: string) {
  return api.get<Rotator>(`/manager-api/rotators/${encodeURIComponent(id)}`)
}

export function createRotator(data: CreateRotatorInput) {
  return api.post<Rotator>('/manager-api/rotators', data)
}

export function updateRotator(id: string, data: CreateRotatorInput) {
  return api.put<Rotator>(`/manager-api/rotators/${encodeURIComponent(id)}`, data)
}

export function deleteRotator(id: string) {
  return api.delete(`/manager-api/rotators/${encodeURIComponent(id)}`)
}

// Exported for stores that need to issue ad-hoc requests (e.g. dynamic
// REST paths the typed helpers don't cover). Always preferred to creating
// a separate axios instance because this one carries the JWT interceptor.
export { api }
export default api
